import React, { useEffect, useState } from "react";

import useApi from "../../CustomHooks/useApi";
import { ufgAPI } from "../../../api/apiClient";
import Loading from "../../../Loading";

import ConfirmWithPhrasePopup from "../../Common/ConfirmWithPhrasePopup";
import TfgDashboardCard from "./TfgDashboardCard";
import TfgExportsHistory from "./TfgExportsHistory";
import TfgPreviewDialog from "./TfgPreviewDialog";

import "../../../css/Views/TfgCommon.css";
import "../../../css/Views/TfgReportingView.css";

const HISTORY_PAGE_SIZE = 10;

const TfgReportingView = () => {
  const {
    callApi: callDashboardApi,
    isLoading: isDashboardLoading,
  } = useApi();

  const {
    callApi: callDownloadApi,
    isLoading: isDownloading,
  } = useApi();

  const {
    callApi: callHistoryApi,
    isLoading: isHistoryLoading,
  } = useApi();

  const {
    callApi: callGenerateApi,
    isLoading: isGenerating,
  } = useApi();

  const {
    callApi: callExportActionApi,
    isLoading: isExportActionLoading,
  } = useApi();

  const [dashboard, setDashboard] = useState(null);

  const [exportsHistory, setExportsHistory] = useState({
    items: [],
    page: 1,
    pageSize: HISTORY_PAGE_SIZE,
    totalItems: 0,
    totalPages: 0,
    hasPreviousPage: false,
    hasNextPage: false,
  });

  const [historyPage, setHistoryPage] = useState(1);

  const [isPreviewOpen, setIsPreviewOpen] =
    useState(false);

  const [
    isUpdatingExportStatus,
    setIsUpdatingExportStatus,
  ] = useState(false);

  const [isCreatingExport, setIsCreatingExport] =
    useState(false);

  const [
    pendingExportAction,
    setPendingExportAction,
  ] = useState(null);

  const loadDashboard = async () => {
    await callDashboardApi(
      () => ufgAPI.getTfgDashboard(),
      setDashboard
    );
  };

  const loadExportsHistory = async (
    page = historyPage
  ) => {
    await callHistoryApi(
      () =>
        ufgAPI.getTfgExports(
          page,
          HISTORY_PAGE_SIZE
        ),
      setExportsHistory
    );
  };

  const refreshTfgData = async (
    page = historyPage
  ) => {
    await Promise.all([
      loadDashboard(),
      loadExportsHistory(page),
    ]);
  };

  const downloadExport = async (
    exportId,
    fileName
  ) => {
    if (!exportId) {
      return false;
    }

    let wasDownloaded = false;

    await callDownloadApi(
      () => ufgAPI.downloadTfgExport(exportId),
      (blob) => {
        const objectUrl =
          window.URL.createObjectURL(blob);

        const link =
          document.createElement("a");

        link.href = objectUrl;
        link.download =
          fileName ||
          `TFG_export_${exportId}.csv`;

        document.body.appendChild(link);
        link.click();
        link.remove();

        window.URL.revokeObjectURL(objectUrl);

        wasDownloaded = true;
      }
    );

    return wasDownloaded;
  };

  const handleGenerateExport = async () => {
    setIsCreatingExport(true);

    try {
      let createdExport = null;

      await callGenerateApi(
        () => ufgAPI.createTfgExport(),
        (result) => {
          createdExport = result;
        }
      );

      if (!createdExport) {
        return;
      }

      setIsPreviewOpen(false);
      setHistoryPage(1);

      await refreshTfgData(1);
    } finally {
      setIsCreatingExport(false);
    }
  };

  const handleDownloadExport = async (
    exportId,
    fileName
  ) => {
    await downloadExport(exportId, fileName);
  };

  const handleRequestGenerateExport = () => {
    setPendingExportAction({
      type: "generate",
    });
  };

  const handleRequestAcceptExport = (
    exportId,
    fileName
  ) => {
    if (!exportId) {
      return;
    }

    setPendingExportAction({
      type: "accept",
      exportId,
      fileName,
    });
  };

  const handleRequestRejectExport = (
    exportId,
    fileName
  ) => {
    if (!exportId) {
      return;
    }

    setPendingExportAction({
      type: "reject",
      exportId,
      fileName,
    });
  };

  const handleAcceptExport = async (
    exportId
  ) => {
    if (!exportId) {
      return;
    }

    setIsUpdatingExportStatus(true);

    try {
      let wasAccepted = false;

      await callExportActionApi(
        () =>
          ufgAPI.acceptTfgExport(exportId),
        () => {
          wasAccepted = true;
        }
      );

      if (!wasAccepted) {
        return;
      }

      setPendingExportAction(null);

      await refreshTfgData(historyPage);
    } finally {
      setIsUpdatingExportStatus(false);
    }
  };

  const handleRejectExport = async (
    exportId
  ) => {
    if (!exportId) {
      return;
    }

    setIsUpdatingExportStatus(true);

    try {
      let wasRejected = false;

      await callExportActionApi(
        () =>
          ufgAPI.rejectTfgExport(exportId),
        () => {
          wasRejected = true;
        }
      );

      if (!wasRejected) {
        return;
      }

      setPendingExportAction(null);

      await refreshTfgData(historyPage);
    } finally {
      setIsUpdatingExportStatus(false);
    }
  };

  const confirmPendingExportAction =
    async () => {
      if (!pendingExportAction) {
        return;
      }

      if (
        pendingExportAction.type === "generate"
      ) {
        setPendingExportAction(null);
        await handleGenerateExport();
        return;
      }

      if (
        pendingExportAction.type === "accept"
      ) {
        await handleAcceptExport(
          pendingExportAction.exportId
        );
        return;
      }

      if (
        pendingExportAction.type === "reject"
      ) {
        await handleRejectExport(
          pendingExportAction.exportId
        );
      }
    };

  const getConfirmationText = () => {
    if (!pendingExportAction) {
      return null;
    }

    switch (pendingExportAction.type) {
      case "generate":
        return (
          <>
            Czy na pewno chcesz wygenerować
            nowy eksport TFG?
            <br />
            <br />
            Po wygenerowaniu plik będzie
            oczekiwał na pobranie, wczytanie do
            UFG oraz oznaczenie wyniku operacji.
          </>
        );

      case "accept":
        return (
          <>
            Czy na pewno chcesz oznaczyć
            eksport{" "}
            <strong>
              {pendingExportAction.fileName}
            </strong>{" "}
            jako wczytany poprawnie do UFG?
            <br />
            <br />
            Po zatwierdzeniu dane z tego
            eksportu zostaną uznane za
            przekazane do UFG.
          </>
        );

      case "reject":
        return (
          <>
            Czy na pewno chcesz oznaczyć
            eksport{" "}
            <strong>
              {pendingExportAction.fileName}
            </strong>{" "}
            jako odrzucony?
            <br />
            <br />
            Eksport pozostanie w historii, ale
            nie zostanie uznany za poprawnie
            przekazany do UFG.
          </>
        );

      default:
        return null;
    }
  };

  const getConfirmationPhrase = () => {
    switch (pendingExportAction?.type) {
      case "generate":
        return "generuję";

      case "accept":
        return "zatwierdzam";

      case "reject":
        return "odrzucam";

      default:
        return "";
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  useEffect(() => {
    loadExportsHistory(historyPage);
  }, [historyPage]);

  if (
    isDashboardLoading &&
    !dashboard
  ) {
    return (
      <Loading text="Ładowanie danych TFG..." />
    );
  }

  if (!dashboard) {
    return (
      <div className="ufg-content">
        <div className="tfg-load-error">
          <span>
            Nie udało się pobrać danych TFG.
          </span>

          <button
            type="button"
            className="tfg-button tfg-button--primary"
            onClick={() =>
              refreshTfgData(historyPage)
            }
            disabled={isDashboardLoading}
          >
            Spróbuj ponownie
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="ufg-content">
      <TfgDashboardCard
        dashboard={dashboard}
        isRefreshing={isDashboardLoading}
        isDownloading={isDownloading}
        isGenerating={
          isGenerating || isCreatingExport
        }
        isExportActionLoading={
          isExportActionLoading ||
          isUpdatingExportStatus
        }
        onRefresh={() =>
          refreshTfgData(historyPage)
        }
        onOpenPreview={() =>
          setIsPreviewOpen(true)
        }
        onGenerateExport={
          handleRequestGenerateExport
        }
        onDownloadExport={
          handleDownloadExport
        }
        onAcceptExport={
          handleRequestAcceptExport
        }
        onRejectExport={
          handleRequestRejectExport
        }
      />

      <TfgExportsHistory
        exports={exportsHistory.items}
        pagination={exportsHistory}
        isLoading={isHistoryLoading}
        isActionLoading={
          isExportActionLoading ||
          isUpdatingExportStatus
        }
        onRefresh={() =>
          loadExportsHistory(historyPage)
        }
        onDownload={handleDownloadExport}
        onPageChange={setHistoryPage}
      />

      {isPreviewOpen && (
        <TfgPreviewDialog
          onClose={() =>
            setIsPreviewOpen(false)
          }
        />
      )}

      {pendingExportAction && (
        <ConfirmWithPhrasePopup
          text={getConfirmationText()}
          phrase={getConfirmationPhrase()}
          onConfirm={
            confirmPendingExportAction
          }
          onCancel={() =>
            setPendingExportAction(null)
          }
        />
      )}
    </div>
  );
};

export default TfgReportingView;